// src/views/JobProfitabilityDetailView.js
import React, { useState, useEffect, useMemo } from 'react';
import LineDescription from '../components/documents/LineDescription'; // linetext-4d8b2e61 — line descriptions keep line breaks, indents and bullets
import { LINE_NAME, LINE_DESC, LINE_NUM } from '../components/documents/lineItemStyles'; // linetext-4d8b2e61 — the PDF's line layout
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { doc, getDoc, collection, query, where, onSnapshot } from 'firebase/firestore';
import { ChevronLeft, FileText, DollarSign, Percent, Briefcase, Clock } from 'lucide-react';
import { formatCurrency, formatJobNumber } from '../utils/formatters'; // formatJobNumber: jobnumdisp-9b7e4c26
import { canSeeLaborCost, rowsWithCostRates } from '../utils/jobCosts'; // s6costing-4d8a2f63
import { useJobCosts } from '../hooks/useJobCosts'; // s6costing-4d8a2f63
import { netInvoiceTotal, revenueBridge, jobOwnRevenueLines, revenueRemainder } from '../utils/invoiceNetRevenue'; // netrev-6f2a8c53 (R1d-5) - revenue net of credit notes

const StatCard = ({ title, value, icon, color }) => (
    <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex items-center">
            <div className={`p-3 rounded-full mr-4 ${color.bg}`}>
                {React.cloneElement(icon, { size: 24, className: color.text })}
            </div>
            <div>
                <p className="text-sm text-gray-500">{title}</p>
                <p className="text-2xl font-bold">{value}</p>
            </div>
        </div>
    </div>
);

const JobProfitabilityDetailView = ({ currentUserProfile }) => {
    const { jobId } = useParams();
    const navigate = useNavigate();
    const [job, setJob] = useState(null);
    const [linkedInvoices, setLinkedInvoices] = useState([]);
    const [legacyInvoices, setLegacyInvoices] = useState([]);
    const [pointerInvoice, setPointerInvoice] = useState(null);
    const [expenses, setExpenses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!jobId || !currentUserProfile?.organizationId) return;
        const orgId = currentUserProfile.organizationId;

        // Fetch the specific job
        const jobRef = doc(db, `organizations/${orgId}/jobs`, jobId);
        const unsubJob = onSnapshot(jobRef, doc => {
            setJob({ ...doc.data(), id: doc.id }); // s6costing-docid: doc id wins over a stale stored `id` field
        });

        // Fetch ALL linked invoices. Web-created invoices carry `linkedJobId` only
        // (createNewInvoiceFromSource never writes `jobId`); mobile "Complete &
        // Invoice" writes both. A job can also have several invoices (interim +
        // final labor billing), so no limit(1). Merged by doc id below.
        const invoicesRef = collection(db, `organizations/${orgId}/invoices`);
        const unsubLinked = onSnapshot(query(invoicesRef, where('linkedJobId', '==', jobId)), snapshot => {
            setLinkedInvoices(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        const unsubLegacy = onSnapshot(query(invoicesRef, where('jobId', '==', jobId)), snapshot => {
            setLegacyInvoices(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        // Fetch linked expenses
        const expensesRef = collection(db, `organizations/${orgId}/expenses`);
        const qExpenses = query(expensesRef, where('jobId', '==', jobId));
        const unsubExpenses = onSnapshot(qExpenses, snapshot => {
            setExpenses(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        setIsLoading(false);

        return () => {
            unsubJob();
            unsubLinked();
            unsubLegacy();
            unsubExpenses();
        };

    }, [jobId, currentUserProfile]);

    // Some jobs (mobile "Complete & Invoice", batchCreateInvoices) store a direct
    // forward pointer job.invoiceId. Resolve it explicitly — it catches invoices
    // whose back-reference fields don't match this job's id.
    useEffect(() => {
        const orgId = currentUserProfile?.organizationId;
        if (!job?.invoiceId || !orgId) { setPointerInvoice(null); return; }
        let cancelled = false;
        getDoc(doc(db, `organizations/${orgId}/invoices`, job.invoiceId)).then(snap => {
            if (!cancelled && snap.exists()) setPointerInvoice({ id: snap.id, ...snap.data() });
        }).catch(() => { /* pointer is best-effort; back-reference queries still run */ });
        return () => { cancelled = true; };
    }, [job?.invoiceId, currentUserProfile]);

    // One invoice can arrive via several sources (docs carry both link fields, or
    // the forward pointer overlaps a query) — the Map de-dupes by doc id.
    const invoices = useMemo(() => {
        const byId = new Map();
        [...legacyInvoices, ...linkedInvoices, ...(pointerInvoice ? [pointerInvoice] : [])]
            .forEach(inv => byId.set(inv.id, inv));
        return Array.from(byId.values());
    }, [linkedInvoices, legacyInvoices, pointerInvoice]);

    // s6costing-4d8a2f63 — S6b: this job's labour cost rates come from jobCosts (Owner/Admin/
    // Manager only), falling back to a not-yet-migrated row's own rate. Display-only copy.
    const { rates: jobCostRates } = useJobCosts(currentUserProfile?.organizationId, jobId, canSeeLaborCost(currentUserProfile));
    const costRows = useMemo(() => rowsWithCostRates(job?.jobCosting?.laborEntries, jobCostRates), [job, jobCostRates]);

    const reportData = useMemo(() => {
        if (!job) return null;

        // Revenue mirrors the aggregate report, which reads the denormalized
        // job.invoiceTotal scalar — many jobs carry that scalar with no discoverable
        // invoice doc (migration, QuickBooks import, self-computed line items). When
        // we DO find invoices, sum them (accurate for interim + final staged billing);
        // otherwise fall back to the scalar so revenue never shows a false $0.
        const invoiceRevenue = invoices.reduce((total, inv) => total + netInvoiceTotal(inv), 0); // netrev-6f2a8c53 -- less credit notes; parseFloat(inv.total) || 0 for an invoice without one
        const revenue = invoices.length > 0 ? invoiceRevenue : (parseFloat(job.invoiceTotal) || 0);
        const laborCost = costRows.reduce((total, entry) =>
            total + (parseFloat(entry.hours) || 0) * (parseFloat(entry.rate) || 0), 0) || 0; // s6costing-4d8a2f63
        const materialCost = expenses.reduce((total, exp) =>
            total + (parseFloat(exp.amount) || 0), 0);

        const totalCost = materialCost + laborCost;
        const profit = revenue - totalCost;
        const profitMargin = revenue > 0 ? (profit / revenue) * 100 : 0;

        // Three-axis split: labor billed to the customer vs. everything else.
        // Invoices with no source:'labor' lines are valid history (invoiced
        // before labor billing launched, or the org has it switched off) —
        // hasLaborLines gates the breakdown so they never read as $0 labor.
        const allLineItems = invoices.flatMap(inv => inv.lineItems || []);
        const lineValue = li => (parseFloat(li.quantity) || 0) * (parseFloat(li.price) || 0);
        const laborLines = allLineItems.filter(li => li.source === 'labor');
        const otherLines = allLineItems.filter(li => li.source !== 'labor');
        const laborRevenue = laborLines.reduce((total, li) => total + lineValue(li), 0);
        const lineItemRevenue = otherLines.reduce((total, li) => total + lineValue(li), 0);

        // jobadds-5d1b9c37 -- no invoice found: revenue is the job's own price (above), so the page
        // lists the job's own line items and its tax instead of an empty list.
        const ownLines = invoices.length > 0 ? null : jobOwnRevenueLines(job, revenue);
        const bridge = ownLines ? { tax: ownLines.tax, credits: [] } : revenueBridge(invoices);

        return {
            revenue, laborCost, materialCost, totalCost, profit, profitMargin,
            laborLines, otherLines, laborRevenue, lineItemRevenue,
            laborMargin: laborRevenue - laborCost,
            hasLaborLines: laborLines.length > 0,
            // revbridge-3c9e7a15 -- the tax and the credit notes between the items above and Total Revenue.
            revenueBridge: bridge,
            // jobadds-5d1b9c37 -- a job with no invoice lists its OWN line items; and whatever the lines
            // still don't explain is shown as one "Other" line, so the page always adds up.
            ownLines,
            revenueOther: revenueRemainder(revenue, ownLines ? ownLines.items.reduce((t, i) => t + i.amount, 0) : laborRevenue + lineItemRevenue, bridge.tax, bridge.credits),
        };
    }, [job, invoices, expenses, costRows]);

    if (isLoading || !reportData) {
        return <div className="p-10 text-center">Loading report...</div>;
    }

    return (
        <div className="p-6 md:p-10">
            <button onClick={() => navigate(`/reports/profitability?expand=${jobId}`)} className="flex items-center text-sm font-semibold text-blue-600 hover:text-blue-800 mb-6">
                <ChevronLeft size={16} className="mr-1" />
                Back to Profitability Report
            </button>
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-800">{job.title || 'Job Details'}</h1>
                {/* jobnumdisp-9b7e4c26 — no number (non-jobs since Stage 1) shows just the client, never "# - Client" */}
                <p className="text-gray-500">{formatJobNumber(job.jobNumber) ? `${formatJobNumber(job.jobNumber)} - ` : ''}{job.clientName}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
                <StatCard title="Profit" value={formatCurrency(reportData.profit)} icon={<DollarSign />} color={{bg: 'bg-green-100', text: 'text-green-600'}} />
                <StatCard title="Margin" value={`${reportData.profitMargin.toFixed(1)}%`} icon={<Percent />} color={{bg: 'bg-blue-100', text: 'text-blue-600'}} />
                <StatCard title="Revenue" value={formatCurrency(reportData.revenue)} icon={<FileText />} color={{bg: 'bg-gray-100', text: 'text-gray-600'}} />
                <StatCard title="Total Costs" value={formatCurrency(reportData.totalCost)} icon={<Briefcase />} color={{bg: 'bg-red-100', text: 'text-red-600'}} />
            </div>

            {reportData.hasLaborLines && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
                    <StatCard title="Labor Billed" value={formatCurrency(reportData.laborRevenue)} icon={<Clock />} color={{bg: 'bg-purple-100', text: 'text-purple-600'}} />
                    <StatCard title="Labor Cost" value={formatCurrency(reportData.laborCost)} icon={<Clock />} color={{bg: 'bg-red-100', text: 'text-red-600'}} />
                    <StatCard title="Labor Margin" value={formatCurrency(reportData.laborMargin)} icon={<DollarSign />} color={{bg: 'bg-green-100', text: 'text-green-600'}} />
                    <StatCard title="Products & Services" value={formatCurrency(reportData.lineItemRevenue)} icon={<FileText />} color={{bg: 'bg-gray-100', text: 'text-gray-600'}} />
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white shadow-sm rounded-lg p-6">
                    <h3 className="font-bold mb-4">Financial Breakdown</h3>
                    {/* Invoice Line Items */}
                    {reportData.hasLaborLines ? (
                        <>
                            <h4 className="font-semibold text-sm text-gray-600 mt-6 mb-2">Billable Labor (from Invoices)</h4>
                            {reportData.laborLines.map((item, i) => (
                                <div key={i} className="flex justify-between items-start gap-3 py-2 border-b text-sm">
                                    {/* linetext-4d8b2e61 — the PDF's layout: the name in bold, its description under it */}
                                    <div className="min-w-0 break-words">
                                        <p className={LINE_NAME}>{item.name || (item.description ? <LineDescription text={item.description} /> : '')}</p>
                                        {item.name && item.description && <span className={LINE_DESC}><LineDescription text={item.description} /></span>}
                                    </div>
                                    <p className={LINE_NUM}>{formatCurrency(item.quantity * item.price)}</p>
                                </div>
                            ))}
                            <div className="flex justify-between py-2 font-bold"><p>Total Labor Billed</p> <p>{formatCurrency(reportData.laborRevenue)}</p></div>

                            <h4 className="font-semibold text-sm text-gray-600 mt-6 mb-2">Products & Services (from Invoices)</h4>
                            {reportData.otherLines.map((item, i) => (
                                <div key={i} className="flex justify-between items-start gap-3 py-2 border-b text-sm">
                                    <div className="min-w-0 break-words">
                                        <p><span className={LINE_NAME}>{item.name}</span> ({item.quantity} x {formatCurrency(item.price)})</p>
                                        {item.description && <span className={LINE_DESC}><LineDescription text={item.description} /></span>}
                                    </div>
                                    <p className={LINE_NUM}>{formatCurrency(item.quantity * item.price)}</p>
                                </div>
                            ))}
                            <div className="flex justify-between py-2 font-bold"><p>Total Products &amp; Services</p> <p>{formatCurrency(reportData.lineItemRevenue)}</p></div>
                        </>
                    ) : (
                        <>
                            {/* jobwording-2f6c8b41 -- a job with no invoice lists its OWN line items, so the heading says so. */}
                            <h4 className="font-semibold text-sm text-gray-600 mt-6 mb-2">{reportData.ownLines ? 'Revenue Items (from the job)' : 'Revenue Items (from Invoices)'}</h4>
                            {reportData.otherLines.map((item, i) => (
                                <div key={i} className="flex justify-between items-start gap-3 py-2 border-b text-sm">
                                    <div className="min-w-0 break-words">
                                        <p><span className={LINE_NAME}>{item.name}</span> ({item.quantity} x {formatCurrency(item.price)})</p>
                                        {item.description && <span className={LINE_DESC}><LineDescription text={item.description} /></span>}
                                    </div>
                                    <p className={LINE_NUM}>{formatCurrency(item.quantity * item.price)}</p>
                                </div>
                            ))}
                            {invoices.length > 0 && (
                                <p className="text-xs text-gray-400 italic py-1">Labor was not itemized on this job's invoices.</p>
                            )}
                            {/* jobadds-5d1b9c37 -- no invoice yet: the job's own line items (its price is what Total Revenue shows). */}
                            {reportData.ownLines && reportData.ownLines.items.length > 0 && (
                                <>
                                    <p className="text-xs text-gray-400 italic py-1">This job has no invoice yet. These are the job's own line items.</p>
                                    {reportData.ownLines.items.map((item, i) => (
                                        <div key={`own-${i}`} className="flex justify-between items-start gap-3 py-2 border-b text-sm"><p className="min-w-0 break-words"><span className={LINE_NAME}>{item.name}</span> ({item.quantity} x {formatCurrency(item.price)})</p> <p className={LINE_NUM}>{formatCurrency(item.amount)}</p></div>
                                    ))}
                                </>
                            )}
                        </>
                    )}
                    {/* revbridge-3c9e7a15 -- so the lines above add up to Total Revenue: the tax the invoices charged, then each credit note. */}
                    {reportData.revenueBridge.tax && (
                        <div className="flex justify-between py-2 border-b text-sm"><p>{reportData.revenueBridge.tax.label}</p> <p>{formatCurrency(reportData.revenueBridge.tax.amount)}</p></div>
                    )}
                    {reportData.revenueBridge.credits.map((c, i) => (
                        <div key={`cn-${i}`} className="flex justify-between py-2 border-b text-sm text-orange-700"><p>Credit note{c.number ? ` ${c.number}` : ''}</p> <p>−{formatCurrency(c.amount)}</p></div>
                    ))}
                    {reportData.revenueOther !== 0 && (
                        <div className="flex justify-between py-2 border-b text-sm text-gray-600"><p>{reportData.ownLines ? 'Job price not itemized above' : 'Other (invoice amounts not itemized above)'}</p> <p>{reportData.revenueOther < 0 ? '−' : ''}{formatCurrency(Math.abs(reportData.revenueOther))}</p></div>
                    )}
                    <div className="flex justify-between py-2 font-bold"><p>Total Revenue</p> <p>{formatCurrency(reportData.revenue)}</p></div>
                    
                    {/* Labor Entries */}
                    <h4 className="font-semibold text-sm text-gray-600 mt-6 mb-2">Labor Costs</h4>
                    {costRows.map((item, i) => ( /* s6costing-4d8a2f63 */
                        <div key={i} className="flex justify-between py-2 border-b text-sm"><p>{item.name} ({item.hours} hrs @ {formatCurrency(item.rate)}/hr)</p> <p>{formatCurrency(item.hours * item.rate)}</p></div>
                    ))}
                    <div className="flex justify-between py-2 font-bold"><p>Total Labor Cost</p> <p>{formatCurrency(reportData.laborCost)}</p></div>

                     {/* Linked Expenses */}
                    <h4 className="font-semibold text-sm text-gray-600 mt-6 mb-2">Linked Expenses</h4>
                    {expenses.map((item, i) => (
                        <div key={i} className="flex justify-between py-2 border-b text-sm"><p>{item.category} ({item.vendor || 'N/A'})</p> <p>{formatCurrency(item.amount)}</p></div>
                    ))}
                    <div className="flex justify-between py-2 font-bold"><p>Total Expenses</p> <p>{formatCurrency(reportData.materialCost)}</p></div>

                </div>
            </div>
        </div>
    );
};

export default JobProfitabilityDetailView;